// src/services/google-reviews-service.js
// Client-side service for Google Reviews Import.
// Cloud Functions handle all Google Places API calls.
// Client reads cached reviews from Firestore subcollection.

import { db } from '../config/firebase-config';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const REGION = 'us-west1';

// ─── Cloud Function callers ───────────────────────────────────────────────────

/** Search Google Places by business name. Returns top 5 results. */
export async function searchGooglePlaces(searchQuery) {
  const fn = httpsCallable(getFunctions(undefined, REGION), 'searchGooglePlaces');
  const result = await fn({ query: searchQuery });
  return result.data.results;
}

/** Connect a Google Business by Place ID. Fetches and caches reviews. */
export async function connectGoogleBusiness(placeId, businessName) {
  const fn = httpsCallable(getFunctions(undefined, REGION), 'connectGoogleBusiness');
  const result = await fn({ placeId, businessName });
  return result.data;
}

/** Refresh cached Google reviews (rate-limited to once per 24h). */
export async function refreshGoogleReviews() {
  const fn = httpsCallable(getFunctions(undefined, REGION), 'refreshGoogleReviews');
  const result = await fn({});
  return result.data;
}

/** Disconnect Google Business and remove cached reviews. */
export async function disconnectGoogleBusiness() {
  const fn = httpsCallable(getFunctions(undefined, REGION), 'disconnectGoogleBusiness');
  const result = await fn({});
  return result.data;
}

// ─── Real-time Firestore reads ────────────────────────────────────────────────

/**
 * Subscribe to cached Google reviews for a practitioner.
 * Returns unsubscribe function.
 */
export function subscribeToGoogleReviews(practitionerId, onReviews) {
  if (!practitionerId || !db) return () => {};
  const q = query(
    collection(db, 'practitioners', practitionerId, 'googleReviews'),
    orderBy('time', 'desc')
  );
  return onSnapshot(q, (snap) => {
    onReviews(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (err) => {
    console.error('subscribeToGoogleReviews error:', err.message);
  });
}

/**
 * One-time fetch of Google reviews for a practitioner's public site.
 * Used by PublicWebsite.js (fire-and-forget after page load).
 */
export async function getGoogleReviews(practitionerId) {
  const { getDocs } = await import('firebase/firestore');
  const q = query(
    collection(db, 'practitioners', practitionerId, 'googleReviews'),
    orderBy('time', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
